<template>
	<nk-wrapper v-if="context.kontaktperson" spacing="lg">
		<nk-row vertical-gutter="3">
			<nk-col
				v-if="context.kontaktperson.bildUrl"
				cols="full"
				sm="shrink"
				order-sm="2"
				class="hidden sm:block"
			>
				<nk-image
					:src="context.kontaktperson.bildUrl"
					:alt="`Kontaktperson ${context.kontaktperson.vorname} ${context.kontaktperson.nachname}`"
					image-class="w-full sm:w-40 lg:w-56"
				/>
			</nk-col>
			<nk-col cols="full" sm="auto" order-sm="1">
				<nk-title size="4xl" tag="h1">
					{{ context.kontaktperson.vorname }}
					{{ context.kontaktperson.nachname }}
				</nk-title>

				<nk-wrapper>
					<div class="text-md" v-html="context.kontaktperson.funktion" />
				</nk-wrapper>

				<nk-wrapper v-if="context.kontaktperson.bildUrl">
					<nk-image
						class="sm:hidden"
						:src="context.kontaktperson.bildUrl"
						:alt="`Kontaktperson ${context.kontaktperson.vorname} ${context.kontaktperson.nachname}`"
						image-class="w-full"
					/>
				</nk-wrapper>

				<!-- Überschrift für Adresse & Sprechzeiten -->
				<nk-wrapper
					v-if="context.kontaktperson.kommunikationsmoeglichkeiten.length"
					spacing="md"
				>
					<nk-title
						v-if="context.kontaktperson.kommunikationsmoeglichkeiten.length"
						size="lg"
						tag="h2"
					>
						Sie erreichen mich persönlich:
					</nk-title>

					<!-- Adresse -->
					<nk-address
						variant="vertical"
						:email="email"
						:telefax="fax"
						:telefon="
							telefonFromKontaktmoeglichkeiten.length === 0 ? telefon : null
						"
						:mobil="mobiltelefon"
						:zimmer="context.kontaktperson.raum"
						:gebaeude="context.kontaktperson.gebaeude"
					/>
				</nk-wrapper>
				<nk-wrapper
					v-if="telefonFromKontaktmoeglichkeiten.length > 0"
					spacing="md"
				>
					<div
						v-for="(
							telefonFromKontaktmoeglichkeit, index
						) in telefonFromKontaktmoeglichkeiten"
						:key="`telefonFromKontaktmoeglichkeiten-${index}`"
					>
						<strong
							v-if="
								telefonFromKontaktmoeglichkeit.kennungszusatz != null &&
								telefonFromKontaktmoeglichkeit.kennungszusatz.length > 0
							"
							class="more-numbers-label"
						>
							{{ telefonFromKontaktmoeglichkeit.kennungszusatz }}
						</strong>

						<nk-address
							:telefon="telefonFromKontaktmoeglichkeit.kennung"
							class="more-numbers"
						/>
					</div>
				</nk-wrapper>

				<!-- Überschrift für Adresse & Sprechzeiten -->
				<nk-wrapper
					v-if="context.kontaktperson.sprechzeiten.text.length > 0"
					spacing="lg"
				>
					<nk-title size="lg" tag="h2">Sprechzeiten</nk-title>
					<div
						class="nk-cms-content"
						v-html="context.kontaktperson.sprechzeiten.text"
					/>
				</nk-wrapper>
			</nk-col>
		</nk-row>
		<nk-wrapper spacing="lg">
			<nk-accordion
				v-if="
					context.kontaktperson.zustaendigkeit ||
					context.kontaktperson.zustaendigkeiten
				"
				v-slot="{titleId: accordionTitleId}"
				title="Zuständigkeiten"
			>
				<div
					v-if="context.kontaktperson.zustaendigkeit"
					v-html="context.kontaktperson.zustaendigkeit"
				/>
				<template v-if="context.kontaktperson.zustaendigkeiten">
					<nk-nav
						v-for="(zustaendigkeit, zustaendigkeitIndex) in context
							.kontaktperson.zustaendigkeiten"
						:key="zustaendigkeitIndex"
						:aria-labelledby="accordionTitleId"
					>
						<nk-nav-item
							:link="`${
								context.taggingpfadLeistungen.slice(-1) !== '/'
									? context.taggingpfadLeistungen + '/'
									: context.taggingpfadLeistungen
							}${zustaendigkeit.leistung.name[0].toUpperCase()}/${
								$slugify(zustaendigkeit.leistung.name) +
								'-id_' +
								zustaendigkeit.leistung.id
							}`"
						>
							{{ zustaendigkeit.leistung.name }}
						</nk-nav-item>
					</nk-nav>
				</template>
			</nk-accordion>
		</nk-wrapper>

		<nk-wrapper
			v-if="
				context.kontaktperson.organisationseinheiten &&
				context.kontaktperson.organisationseinheiten.length > 0
			"
			spacing="lg"
		>
			<nk-title :id="`title-${titleId}`" tag="h2" size="xl">
				In folgenden Fachbereichen bin ich für Sie tätig:
			</nk-title>

			<nk-nav :aria-labelledby="`title-${titleId}`" variant="links">
				<nk-nav-item
					v-for="(organisationseinheit, index) in context.kontaktperson
						.organisationseinheiten"
					:key="`organisationseinheit-${index}`"
					:link="`${
						context.taggingpfadOrganisationseinheiten.slice(-1) !== '/'
							? context.taggingpfadOrganisationseinheiten + '/'
							: context.taggingpfadOrganisationseinheiten
					}${
						$slugify(organisationseinheit.name) +
						'-id_' +
						organisationseinheit.id
					}`"
				>
					{{ organisationseinheit.name }}
				</nk-nav-item>
			</nk-nav>
		</nk-wrapper>
	</nk-wrapper>
</template>

<script>
import TemplatesDefault from '../../mixins/templates-default';
import {updateBreadcrumb, useSEO, useCMS} from '#nk/composables/cms';
import {ref, useId} from '#imports';

/**
 * @prop {object} context.kontaktperson
 * @prop {object} context.taggingpfadLeistungen
 * @prop {object} context.taggingpfadOrganisationseinheiten
 */
export default {
	name: 'NkTemplateServiceBwKontaktpersonenDetails',

	mixins: [TemplatesDefault],

	setup(ctx) {
		const page = useCMS();
		const kontaktperson = {
			vorname: ctx?.context?.kontaktperson?.vorname || '',
			nachname: ctx?.context?.kontaktperson?.nachname || '',
		};

		if (kontaktperson.vorname && kontaktperson.nachname) {
			updateBreadcrumb({
				title: `${kontaktperson.vorname} ${kontaktperson.nachname}`,
			});
		}

		useSEO({
			title: page.value.title
				? `${page.value.title} ${kontaktperson.vorname} ${kontaktperson.nachname}`
				: `${kontaktperson.vorname} ${kontaktperson.nachname}`,
		});
		const telefonFromKontaktmoeglichkeiten = ref([]);
		telefonFromKontaktmoeglichkeiten.value =
			ctx.context.kontaktperson.kommunikationsmoeglichkeiten.filter(
				data => data.kanal === 'TELEFON',
			);

		const titleId = useId();
		return {
			titleId,
			telefonFromKontaktmoeglichkeiten,
		};
	},

	computed: {
		fax() {
			const fax = this.context.kontaktperson.kommunikationsmoeglichkeiten.find(
				data => data.kanal === 'FAX',
			);
			return fax ? fax.kennung : undefined;
		},
		telefon() {
			const telefon =
				this.context.kontaktperson.kommunikationsmoeglichkeiten.find(
					data => data.kanal === 'TELEFON',
				);
			return telefon ? telefon.kennung : undefined;
		},
		email() {
			const email =
				this.context.kontaktperson.kommunikationsmoeglichkeiten.find(
					data => data.kanal === 'EMAIL',
				);
			return email ? email.kennung : undefined;
		},
		mobiltelefon() {
			const email =
				this.context.kontaktperson.kommunikationsmoeglichkeiten.find(
					data => data.kanal === 'MOBILTELEFON',
				);
			return email ? email.kennung : undefined;
		},
		leistungen() {
			const leistungen = this.context.kontaktperson.zustaendigkeiten
				? this.context.kontaktperson.zustaendigkeiten.map(data => data.leistung)
				: [];
			return leistungen;
		},
	},
};
</script>

<style scoped>
.more-numbers-label {
  margin-right: 0.5rem;
}

.more-numbers {
  display: inline-block !important;
}
.more-numbers * {
  display: inline-block !important;
}
.more-numbers br {
  display: none;
}
</style>
